import React, { useContext, useCallback, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer, Slide } from 'react-toastify';
import { ThemeProvider } from '@mui/material/styles';
import { lightTheme, darkTheme } from './mui/theme';
import { Modules, RouteParamsTypes, AuthRoutes, NonAuthRoutes } from './enums';
import { AuthContext } from './contexts/AuthContext';
import { redirectBasedOnPermission } from './utils/methods';

import Auth from './routes/Auth';
import My from './routes/My';
import Home from './routes/Home';
import Errors from './routes/Errors';
import Masters from './routes/Masters';
import System from './routes/System';
import Iam from './routes/Iam';
import Tenants from './routes/Tenants';

import HttpInterceptor from './components/HttpInterceptor';
import UnHandledRejection from './components/ErrorPages/UnHandledRejection';
import GlobalLoader from './components/GlobalLoader';
import ImpersonationBanner from './components/ImpersonationBanner';
import StatusColourProvider from './providers/status-colour.provider';
import ProductProvider from './providers/product.provider';
import ImpersonationProvider from './providers/impersonation.provider';

import './assets/vendor/fonts/remixicon/remixicon.css';
import './assets/vendor/fonts/flag-icons.css';
import './assets/vendor/libs/node-waves/node-waves.css';
import './assets/css/demo.css';
import './assets/vendor/libs/perfect-scrollbar/perfect-scrollbar.css';
import './assets/vendor/libs/typeahead-js/typeahead.css';
import './assets/vendor/libs/datatables-bs5/datatables.bootstrap5.css';
import './assets/vendor/libs/datatables-responsive-bs5/responsive.bootstrap5.css';
import './assets/vendor/libs/datatables-buttons-bs5/buttons.bootstrap5.css';
import './assets/vendor/libs/datatables-checkboxes-jquery/datatables.checkboxes.css';
import './assets/vendor/libs/select2/select2.css';
import './assets/vendor/libs/@form-validation/form-validation.css';
import './assets/vendor/css/pages/page-auth.css';
import './assets/vendor/css/pages/page-profile.css';
import './assets/app-only/css/style.scss';
import 'react-toastify/dist/ReactToastify.css';

import './i18n/config';

const App: React.FC = () => {
  const { token, theme, permissions, isSystemAdmin, exception, loading, setLoading } =
    useContext(AuthContext);
  const themeLinkRef = useRef<HTMLLinkElement | null>(null);
  const themeLinkRefTwo = useRef<HTMLLinkElement | null>(null);
  const evyasysTokensRef = useRef<HTMLLinkElement | null>(null);
  const evyasysComponentsRef = useRef<HTMLLinkElement | null>(null);

  const loadCSS = (href: string): HTMLLinkElement => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.type = 'text/css';
    document.head.appendChild(link);
    return link;
  };

  useEffect(() => {
    const root = document.documentElement;
    if (themeLinkRef.current) {
      document.head.removeChild(themeLinkRef.current);
    }

    if (themeLinkRefTwo.current) {
      document.head.removeChild(themeLinkRefTwo.current);
    }

    const href = theme === 'light' ? '/theme/light/core.css' : '/theme/dark/core.css';
    const hrefTwo =
      theme === 'light' ? '/theme/light/theme-default.css' : '/theme/dark/theme-default.css';
    const hrefThree = '/theme/responsive.css';
    themeLinkRef.current = loadCSS(href);
    themeLinkRefTwo.current = loadCSS(hrefTwo);
    loadCSS(hrefThree);

    // Evyasys tokens & components must load AFTER template CSS to win cascade
    if (evyasysTokensRef.current) {
      document.head.removeChild(evyasysTokensRef.current);
    }
    if (evyasysComponentsRef.current) {
      document.head.removeChild(evyasysComponentsRef.current);
    }
    evyasysTokensRef.current = loadCSS('/evyasys-tokens.css');
    evyasysComponentsRef.current = loadCSS('/evyasys-components.css');

    root.classList.add(theme === 'light' ? 'light-style' : 'dark-style');
    root.classList.remove(theme === 'light' ? 'dark-style' : 'light-style');

    setTimeout(() => {
      setLoading('false');
    }, 100);
  }, [theme]);

  const handleBlankRoute = useCallback(() => {
    if (!token) {
      return Modules.auth;
    } else {
      return redirectBasedOnPermission(isSystemAdmin, permissions);
    }
  }, [token, isSystemAdmin, permissions]);

  const handleAnyRoute = useCallback(() => {
    if (!token) {
      return Modules.auth;
    }
    return Modules.error + NonAuthRoutes.notFound;
  }, [token]);

  const isLoading = loading === 'true';

  return (
    <ThemeProvider theme={theme === 'light' ? lightTheme : darkTheme}>
      <Router>
        {exception && <UnHandledRejection />}
        {isLoading && <GlobalLoader />}

        <HttpInterceptor />
        <ToastContainer
          position="top-center"
          autoClose={5000}
          transition={Slide}
          newestOnTop
          closeOnClick
          rtl={false}
          draggable={false}
          closeButton={false}
          className="toast-container-custom"
        />

        {!isLoading && (
          <StatusColourProvider>
            <ProductProvider>
              <ImpersonationProvider>
                <ImpersonationBanner />
                <Routes>
                  <Route
                    path={AuthRoutes.blank}
                    element={<Navigate replace to={{ pathname: handleBlankRoute() }} />}
                  />
                  <Route path={Modules.auth + RouteParamsTypes.any} element={<Auth />} />
                  <Route path={Modules.my + RouteParamsTypes.any} element={<My />} />
                  <Route path={Modules.home + RouteParamsTypes.any} element={<Home />} />
                  <Route path={Modules.masters + RouteParamsTypes.any} element={<Masters />} />
                  <Route path={Modules.system + RouteParamsTypes.any} element={<System />} />
                  <Route path={Modules.iam + RouteParamsTypes.any} element={<Iam />} />
                  <Route path={Modules.tenants + RouteParamsTypes.any} element={<Tenants />} />
                  <Route path={Modules.error + RouteParamsTypes.any} element={<Errors />} />
                  <Route
                    path={RouteParamsTypes.any}
                    element={<Navigate replace to={{ pathname: handleAnyRoute() }} />}
                  />
                </Routes>
              </ImpersonationProvider>
            </ProductProvider>
          </StatusColourProvider>
        )}
      </Router>
    </ThemeProvider>
  );
};

export default App;
